/**
 * Shared wallet HUD: HP rail + geo counter + skills strip + inventory rail.
 * Same visual layout in RunnerScene and ShopScene so the market shows the
 * exact same readouts as the active session.
 *
 * The class owns the static visuals only — it deliberately exposes the
 * underlying Rectangles/Images/Texts (`hpRects`, `skillIcons`, `geoText`,
 * `inventorySlots`, `bootIcon`) so the runner can hook animations off them
 * (HP pulse on damage, skill-icon scale tween on unlock, boot alpha pulse
 * while a speed boost is active). The market just calls `update(state)`
 * and gets a static readout — no animations needed there.
 */

import Phaser from "phaser";

import { GAMEPLAY, NATIVE_WIDTH, PALETTE } from "../config";
import type { Skill } from "../config";
import type { InventoryItem } from "../types";
import { t } from "../i18n";

export interface WalletHudState {
  hp: number;
  geo: number;
  inventory: readonly InventoryItem[];
  skills: Record<Skill, boolean>;
  /** True while a Boot speed-boost is in flight. Boot icon brightens. */
  bootBoostActive: boolean;
}

export interface WalletHudOptions {
  /**
   * Optional tooltip wiring. The runner provides this to attach pointer-
   * hover tooltips to each panel; the market omits it. The HUD calls it
   * once per attachment target during build.
   */
  attachTooltip?: (
    target: Phaser.GameObjects.Rectangle | Phaser.GameObjects.Image,
    textProvider: () => string,
  ) => void;
  /**
   * Optional getter for the dynamic tooltip text shown on each panel.
   * Receives the panel id; the runner uses this to surface boost / kill /
   * inventory state. Market can skip.
   */
  tooltipTextFor?: (id: WalletHudTooltipId) => string;
}

export type WalletHudTooltipId = "hp" | "geo" | "skill.jump" | "skill.doubleJump" | "skill.attack" | "skill.interact" | "skill.boot" | "inventory";

const SKILL_ORDER: Skill[] = ["jump", "doubleJump", "attack", "interact"];

const SKILL_TEXTURE: Record<Skill, string> = {
  jump: "icon.jump",
  doubleJump: "icon.dblJump",
  attack: "icon.attack",
  interact: "icon.interact",
};

export class WalletHud {
  readonly hpPanel: Phaser.GameObjects.Rectangle;
  readonly hpRects: Phaser.GameObjects.Rectangle[] = [];
  readonly hpLabel: Phaser.GameObjects.Text;
  readonly geoPanel: Phaser.GameObjects.Rectangle;
  readonly geoLabel: Phaser.GameObjects.Text;
  readonly geoText: Phaser.GameObjects.Text;
  readonly skillsPanel: Phaser.GameObjects.Rectangle;
  readonly skillIcons: Partial<Record<Skill, Phaser.GameObjects.Image>> = {};
  readonly bootIcon: Phaser.GameObjects.Image;
  readonly invPanel: Phaser.GameObjects.Rectangle;
  readonly inventorySlots: Phaser.GameObjects.Image[] = [];
  readonly inventoryItemSprites: (Phaser.GameObjects.Image | undefined)[] = [];

  constructor(private readonly scene: Phaser.Scene, options: WalletHudOptions = {}) {
    const tip = (target: Phaser.GameObjects.Rectangle | Phaser.GameObjects.Image, id: WalletHudTooltipId): void => {
      if (!options.attachTooltip || !options.tooltipTextFor) return;
      options.attachTooltip(target, () => options.tooltipTextFor!(id));
    };

    // HP frame, top-left.
    this.hpPanel = scene.add
      .rectangle(4, 4, 70, 12, PALETTE.hudPanel)
      .setOrigin(0, 0)
      .setStrokeStyle(1, PALETTE.hudDim);
    this.hpLabel = scene.add
      .text(7, 6, t("shop.hp"), {
        fontFamily: "monospace",
        fontSize: "8px",
        color: this.toHex(PALETTE.hudText),
      })
      .setOrigin(0, 0);
    for (let i = 0; i < GAMEPLAY.heroMaxHp; i++) {
      const r = scene.add
        .rectangle(22 + i * 7, 7, 5, 6, PALETTE.hp)
        .setOrigin(0, 0);
      this.hpRects.push(r);
    }
    tip(this.hpPanel, "hp");

    // Geo counter, top-right.
    this.geoPanel = scene.add
      .rectangle(NATIVE_WIDTH - 74, 4, 70, 12, PALETTE.hudPanel)
      .setOrigin(0, 0)
      .setStrokeStyle(1, PALETTE.hudDim);
    this.geoLabel = scene.add
      .text(NATIVE_WIDTH - 71, 6, "geo", {
        fontFamily: "monospace",
        fontSize: "8px",
        color: this.toHex(PALETTE.hudDim),
      })
      .setOrigin(0, 0);
    this.geoText = scene.add
      .text(NATIVE_WIDTH - 7, 6, "0", {
        fontFamily: "monospace",
        fontSize: "8px",
        color: this.toHex(PALETTE.geo),
      })
      .setOrigin(1, 0);
    tip(this.geoPanel, "geo");

    // Skills strip, just under HP frame.
    this.skillsPanel = scene.add
      .rectangle(4, 18, 74, 14, PALETTE.hudPanel)
      .setOrigin(0, 0)
      .setStrokeStyle(1, PALETTE.hudDim);
    SKILL_ORDER.forEach((s, i) => {
      const icon = scene.add.image(10 + i * 14, 25, SKILL_TEXTURE[s]).setOrigin(0.5, 0.5);
      this.skillIcons[s] = icon;
      tip(icon, `skill.${s}` as WalletHudTooltipId);
    });
    this.bootIcon = scene.add.image(10 + 4 * 14, 25, "icon.boot").setOrigin(0.5, 0.5);
    tip(this.bootIcon, "skill.boot");

    // Inventory strip, top-right under geo counter.
    this.invPanel = scene.add
      .rectangle(NATIVE_WIDTH - 50, 18, 46, 14, PALETTE.hudPanel)
      .setOrigin(0, 0)
      .setStrokeStyle(1, PALETTE.hudDim);
    for (let i = 0; i < GAMEPLAY.inventorySlots; i++) {
      const slot = scene.add
        .image(NATIVE_WIDTH - 44 + i * 14, 25, "inv.slot")
        .setOrigin(0.5, 0.5);
      this.inventorySlots.push(slot);
      this.inventoryItemSprites.push(undefined);
    }
    tip(this.invPanel, "inventory");
  }

  /** Apply a full wallet state update. Idempotent; safe to call every frame. */
  update(state: WalletHudState): void {
    this.hpRects.forEach((r, i) => {
      r.fillColor = i < state.hp ? PALETTE.hp : PALETTE.hpEmpty;
    });
    this.geoText.setText(String(state.geo));
    // Skill icons — locked tint + 40% alpha when not yet owned.
    for (const s of SKILL_ORDER) {
      const icon = this.skillIcons[s];
      if (!icon) continue;
      if (state.skills[s]) {
        icon.clearTint();
        icon.setAlpha(1);
      } else {
        icon.setTint(PALETTE.skillLocked);
        icon.setAlpha(0.4);
      }
    }
    // Boot slot — bright + cleared tint while a speed boost is active;
    // otherwise dim/locked. (Pulse animation lives on the scene since it
    // depends on a tween manager tied to scene lifetime.)
    if (state.bootBoostActive) {
      this.bootIcon.clearTint();
      this.bootIcon.setAlpha(1);
    } else {
      this.bootIcon.setTint(PALETTE.skillLocked);
      this.bootIcon.setAlpha(0.4);
    }
    // Inventory items.
    this.refreshInventory(state.inventory);
  }

  /** Re-render the inventory slot sprites. Public so the runner can call it
   *  on pickup/removal without going through a full `update()`. */
  refreshInventory(items: readonly InventoryItem[]): void {
    for (let i = 0; i < GAMEPLAY.inventorySlots; i++) {
      const item = items[i];
      const existing = this.inventoryItemSprites[i];
      if (existing) {
        existing.destroy();
        this.inventoryItemSprites[i] = undefined;
      }
      if (!item) continue;
      const slot = this.inventorySlots[i];
      if (!slot) continue;
      const key = item.kind === "scroll" ? "scroll" : "geo";
      const sprite = this.scene.add.image(slot.x, slot.y, key).setOrigin(0.5, 0.5);
      this.inventoryItemSprites[i] = sprite;
    }
  }

  /** Set the HUD's depth uniformly; useful when other UI floats above it. */
  setDepth(depth: number): void {
    this.hpPanel.setDepth(depth);
    this.hpLabel.setDepth(depth);
    this.hpRects.forEach((r) => r.setDepth(depth));
    this.geoPanel.setDepth(depth);
    this.geoLabel.setDepth(depth);
    this.geoText.setDepth(depth);
    this.skillsPanel.setDepth(depth);
    for (const s of SKILL_ORDER) this.skillIcons[s]?.setDepth(depth);
    this.bootIcon.setDepth(depth);
    this.invPanel.setDepth(depth);
    this.inventorySlots.forEach((s) => s.setDepth(depth));
    this.inventoryItemSprites.forEach((s) => s?.setDepth(depth));
  }

  /** World-space location to fly geo "coins" to/from on a market trade. */
  geoAnchor(): { x: number; y: number } {
    return { x: this.geoText.x - 6, y: this.geoText.y + 4 };
  }

  /** World-space location of HP slot `i` — used by trade-fly animations. */
  hpAnchor(i: number): { x: number; y: number } {
    const slot = this.hpRects[i] ?? this.hpRects[0];
    return slot ? { x: slot.x + 2, y: slot.y + 3 } : { x: 30, y: 10 };
  }

  /** World-space location of inventory slot `i`. */
  inventoryAnchor(i: number): { x: number; y: number } {
    const slot = this.inventorySlots[i] ?? this.inventorySlots[0];
    return slot ? { x: slot.x, y: slot.y } : { x: NATIVE_WIDTH - 44, y: 25 };
  }

  /** World-space location of the boot/feather indicator. Used as a generic
   *  "feather charges" anchor for fly animations since the strip doesn't
   *  carry a dedicated feather slot. */
  bootAnchor(): { x: number; y: number } {
    return { x: this.bootIcon.x, y: this.bootIcon.y };
  }

  /** World-space location of the named skill icon. */
  skillAnchor(s: Skill): { x: number; y: number } | undefined {
    const icon = this.skillIcons[s];
    return icon ? { x: icon.x, y: icon.y } : undefined;
  }

  private toHex(c: number): string {
    return `#${c.toString(16).padStart(6, "0")}`;
  }
}
